// src/features/auth/components/LoginForm.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { TextField, Button, Typography, Box, CircularProgress, Paper, Alert } from '@mui/material';
import { loginUser } from '../api/authApi';
import useAuthStore from '../../../store/authStore';
import clarityService from '../../../services/clarityService';
import { SocialLoginButtons } from '../../../components/auth';
import PasswordField from '../../../components/auth/PasswordField';
import { OAuthError } from '../../../types/oauth';
import { User } from '../../../services/authService';
import { brand } from '../../../config/brand';
import { getAppCallback, redirectToApp, resolveRedirect } from '../utils/redirectToApp';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error } = useAuthStore();

  // Desktop/Electron app OAuth flow
  const appCallback = getAppCallback(location.search);

  // Redirect back to the page the user was trying to access, or default to dashboard
  const redirectTo = resolveRedirect(location.state, location.search);

  // Why the visitor is here: expired session (from ProtectedRoute/apiClient)
  const reason =
    (location.state as { reason?: string } | null)?.reason ||
    new URLSearchParams(location.search).get('reason');

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState({ email: '', password: '' });
  const [oauthError, setOauthError] = useState<string | null>(null);

  const finish = (user: User) => {
    if (redirectToApp(appCallback, user)) return;
    navigate(redirectTo, { replace: true });
  };

  // Handle OAuth login success
  const handleOAuthSuccess = (user: User, isNewUser?: boolean) => {
    if (isNewUser) {
      clarityService.track('oauth_signup_success', 'login_page');
      clarityService.upgrade('New User OAuth Registration');
    } else {
      clarityService.track('oauth_login_success');
    }
    finish(user);
  };

  // Handle OAuth login error
  const handleOAuthError = (err: OAuthError) => {
    clarityService.track('oauth_failed', 'login');
    setOauthError(err.message);
  };

  // Validate form
  const validateForm = (): boolean => {
    let valid = true;
    const newErrors = { email: '', password: '' };

    if (!email) {
      newErrors.email = 'Enter your email address';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'That does not look like an email address';
      valid = false;
    }

    if (!password) {
      newErrors.password = 'Enter your password';
      valid = false;
    }

    setFormErrors(newErrors);
    return valid;
  };

  // Handle login submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const user = await loginUser({ email, password });
      clarityService.track('login_success');
      finish(user);
    } catch (err) {
      clarityService.track('login_failed');
      // Error is handled in the API layer and stored in the auth store
      console.error('Login failed:', err instanceof Error ? err.message : String(err));
    }
  };

  const registerHref = `/register${appCallback ? `?app_callback=${encodeURIComponent(appCallback)}` : ''}`;

  return (
    <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, maxWidth: 420, width: '100%' }}>
      <Typography variant="h4" component="h1" align="center" sx={{ fontWeight: 700, mb: 0.5 }}>
        Welcome back
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        {`Sign in to your ${brand.productName} account`}
      </Typography>

      {reason === 'expired' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your session ended. Sign in again to continue where you left off.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* One-tap providers first: they are the fastest path and the one most
          visitors reach for. Email/password follows. */}
      <SocialLoginButtons
        onSuccess={handleOAuthSuccess}
        onError={handleOAuthError}
        disabled={isLoading}
        context="signin"
        showDivider={false}
        error={oauthError}
      />

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', my: 1.5 }}>
          or continue with email
        </Typography>

        <TextField
          margin="dense"
          required
          fullWidth
          id="email"
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!formErrors.email}
          helperText={formErrors.email}
          disabled={isLoading}
        />

        <PasswordField
          margin="dense"
          required
          fullWidth
          name="password"
          label="Password"
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!formErrors.password}
          helperText={formErrors.password}
          disabled={isLoading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          sx={{ mt: 2, mb: 1.5, fontWeight: 700 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Sign in'}
        </Button>

        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
            <Typography variant="body2" color="primary">
              Forgot password?
            </Typography>
          </Link>

          <Link to={registerHref} state={location.state} style={{ textDecoration: 'none' }}>
            <Typography variant="body2" color="primary">
              New here? Create a free account
            </Typography>
          </Link>
        </Box>
      </Box>
    </Paper>
  );
};

export default LoginForm;
